import { addMinutes, differenceInMinutes, format, isPast } from "date-fns";
import { FC, useCallback, useMemo } from "react";

import { ClassSessionSummaryType } from "api/api-data-types";
import { serverGenerateRecordingUrl } from "api/api-server-actions";
import { RecordingSessionState, RecordingSessionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { LinkButton } from "mds/components/LinkButton";
import { Menu, MenuItem } from "mds/components/Menu";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableExportButton,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { DotMenuVerticalIcon, EyeNotVisibleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentCourseTitle,
  selectCurrentUserIsMPAdmin,
  selectRecordingSessionByClassSessionId,
} from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { generateSessionLogCSV } from "utils/reporting";

type SortField =
  | "started_at"
  | "duration"
  | "class_type"
  | "attendance"
  | "student_talk_time"
  | "instructor_talk_time"
  | "your_attendance"
  | "your_time_in_class"
  | "recording"
  | "recording_setup";

const getSortValue = (session: ClassSessionSummaryType, field: SortField) => {
  const duration = differenceInMinutes(session.end_time, session.start_time);
  const isPresent =
    session.student_presence_minutes && duration > 0
      ? session.student_presence_minutes >= duration / 2
      : false;
  switch (field) {
    case "started_at":
      return new Date(session.start_time).getTime();
    case "duration":
      return duration;
    case "class_type":
      return session.view === "virtual-class" ? 1 : 0;
    case "attendance":
      return session.students.length > 0
        ? session.attending_students.length / session.students.length
        : 0;
    case "student_talk_time":
      return session.student_talk_time_seconds ?? 0;
    case "instructor_talk_time":
      return session.instructor_talk_time_seconds ?? 0;
    case "your_attendance":
      return isPresent ? 1 : 0;
    case "your_time_in_class":
      return session.student_presence_minutes ?? 0;
    case "recording":
    case "recording_setup":
      return 0; // Non-sortable fields
    default:
      return 0;
  }
};

function isRecordingAvailable(recordingSession: RecordingSessionType) {
  // We have no deterministic way to know when a recording is finished
  // being processed, so the following calculation makes a guess.
  // Processing of a standard 100 minute recording (90 minutes of class
  // time plus a 10 minute overrun) typically takes 30-35 minutes, but
  // sometimes as long as 40 minutes, so we can make a general assumption
  // processing time might be as much as 40% of recording time.
  if (
    !recordingSession.started_at ||
    !recordingSession.stopped_at ||
    recordingSession.state !== RecordingSessionState.STOPPED
  )
    return false;
  const startTime = new Date(recordingSession.started_at);
  const endTime = new Date(recordingSession.stopped_at);
  const processingTime = 0.4 * differenceInMinutes(endTime, startTime);
  const availableTime = addMinutes(endTime, processingTime);

  return availableTime && isPast(availableTime);
}

async function openRecordingInNewTab(recordingSessionId: string, setup: boolean = false) {
  const url = await serverGenerateRecordingUrl(recordingSessionId, setup);
  window.open(url, "_blank");
}

const isVirtualClassSession = (classSession: ClassSessionSummaryType) => {
  return classSession.view === "virtual-class";
};

const getClassTypeLabel = (classSession: ClassSessionSummaryType) => {
  const view = classSession.view;
  return t(`session_log_view.class_types.${view}`);
};

const formatAttendance = (classSession: ClassSessionSummaryType) => {
  const attendingCount = classSession.attending_students.length;
  const totalCount = classSession.students.length;
  if (totalCount === 0) return "-";
  const percentage = totalCount > 0 ? Math.round((attendingCount / totalCount) * 100) : 0;
  const percentageText = t("common.percentage", { number: percentage });

  return `${attendingCount} / ${totalCount} (${percentageText})`;
};

const formatTalkTime = (
  classSession: ClassSessionSummaryType,
  field: "student_talk_time_seconds" | "instructor_talk_time_seconds",
) => {
  if (!isVirtualClassSession(classSession)) {
    return "-";
  }
  const seconds = classSession[field as keyof ClassSessionSummaryType] as number | undefined;
  if (!seconds) return "-";
  const durationMs =
    new Date(classSession.end_time).getTime() - new Date(classSession.start_time).getTime();
  const minutes = Math.floor(seconds / 60);
  const percentage = durationMs > 0 ? Math.round(((seconds * 1000) / durationMs) * 100) : 0;

  const timeText = t("common.in_minutes", { minutes });
  const percentageText = t("common.percentage", { number: percentage });

  return `${timeText} (${percentageText})`;
};

const formatStudentAttendance = (classSession: ClassSessionSummaryType) => {
  const presenceMinutes = classSession.student_presence_minutes;
  if (presenceMinutes === null || presenceMinutes === undefined) {
    return <span className="text-red">Absent</span>;
  }

  const sessionDurationMinutes = differenceInMinutes(
    classSession.end_time,
    classSession.start_time,
  );
  const halfDuration = sessionDurationMinutes / 2;
  const isPresent = presenceMinutes >= halfDuration;

  return isPresent ? <span>Present</span> : <span className="text-red">Absent</span>;
};

const formatStudentTimeInClass = (classSession: ClassSessionSummaryType) => {
  const presenceMinutes = classSession.student_presence_minutes;
  if (presenceMinutes === null || presenceMinutes === undefined) {
    return "-";
  }
  return t("common.in_minutes", { minutes: presenceMinutes });
};

interface ClassSessionRowProps {
  classSession: ClassSessionSummaryType;
  hasVirtualSessions: boolean;
}

const ClassSessionRow: FC<ClassSessionRowProps> = ({ classSession, hasVirtualSessions }) => {
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const { canAuthorCourse, canParticipateInCourse } = useCapabilities();

  const recordingSession = useAppSelector((state) =>
    selectRecordingSessionByClassSessionId(state, classSession.class_session_id),
  );
  const durationString = useMemo(() => {
    const duration = differenceInMinutes(classSession.end_time, classSession.start_time);
    if (!duration) return "-";
    return t("common.in_minutes", { minutes: duration });
  }, [classSession]);

  const handleHideSession = useCallback(async () => {
    try {
      await storeApi.class_sessions.partial_update({
        id: classSession.class_session_id,
        is_hidden_from_session_logs: true,
      });
    } catch {
      toastErrorMessage(t("session_log_view.hide_session.failed"));
    }
  }, [classSession.class_session_id]);

  const menuButton = (
    <Button
      eventName="Session Log - Options menu"
      kind="transparentSquareLegacy"
      size="s"
      title={t("common.options")}
      isSquare
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </Button>
  );

  if (!classSession.class_session_id) {
    return null;
  }

  return (
    <tr>
      <td>{format(classSession.start_time, "MMM dd yyyy, HH:mm")}</td>
      <td>{durationString}</td>
      <td>{getClassTypeLabel(classSession)}</td>
      {canAuthorCourse && (
        <>
          <td>{formatAttendance(classSession)}</td>
          {hasVirtualSessions && (
            <>
              <td>{formatTalkTime(classSession, "student_talk_time_seconds")}</td>
              <td>{formatTalkTime(classSession, "instructor_talk_time_seconds")}</td>
            </>
          )}
        </>
      )}
      {canParticipateInCourse && (
        <>
          <td>{formatStudentAttendance(classSession)}</td>
          <td>{formatStudentTimeInClass(classSession)}</td>
        </>
      )}
      {hasVirtualSessions && (
        <td>
          {recordingSession && isRecordingAvailable(recordingSession) ? (
            <LinkButton onClick={() => openRecordingInNewTab(recordingSession.id)}>
              {t("session_log_view.watch_recording")}
            </LinkButton>
          ) : recordingSession ? (
            t("session_log_view.not_available")
          ) : (
            "-"
          )}
        </td>
      )}
      {hasVirtualSessions && isMPAdmin && (
        <td>
          {recordingSession && (
            <LinkButton onClick={() => openRecordingInNewTab(recordingSession.id, true)}>
              Watch Recording Setup
            </LinkButton>
          )}
        </td>
      )}
      {canAuthorCourse && (
        <td className="!px-0">
          <div className="flex justify-center">
            <Menu direction="bottom" menuButton={menuButton} menuClassName="min-w-[180px]">
              <MenuItem onClick={handleHideSession}>
                <EyeNotVisibleIcon className="text-black-tint-40" />
                <span className="text-black-tint-20">
                  {t("session_log_view.hide_session.button")}
                </span>
              </MenuItem>
            </Menu>
          </div>
        </td>
      )}
    </tr>
  );
};

interface SessionLogTableProps {
  classSessions: ClassSessionSummaryType[];
}

export const SessionLogTable: FC<SessionLogTableProps> = ({ classSessions }) => {
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const { canAuthorCourse, canParticipateInCourse } = useCapabilities();

  const hasVirtualSessions = useMemo(() => {
    return classSessions.some((session) => isVirtualClassSession(session));
  }, [classSessions]);

  const courseTitle = useAppSelector(selectCurrentCourseTitle);

  let nColumns = 3;
  if (canAuthorCourse) {
    nColumns += 2;
    if (hasVirtualSessions) {
      nColumns += 2;
    }
  }
  if (canParticipateInCourse) {
    nColumns += 2;
  }
  if (hasVirtualSessions) {
    nColumns += 1;
    if (isMPAdmin) {
      nColumns += 1;
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <SortableTableWrapper<ClassSessionSummaryType, SortField>
        className="class-log-dashboard-table"
        data={classSessions}
        defaultSortDirection="desc"
        defaultSortField="started_at"
        getSortValue={getSortValue}
      >
        <div className="mb-4 flex w-full items-center justify-between">
          <h3 className="h3 m-0 text-black-tint-40">{t("session_log_view.title")}</h3>
          {canAuthorCourse && (
            <SortableTableExportButton<ClassSessionSummaryType>
              buttonText={t("session_log_view.export.button")}
              disabledMessage={t("session_log_view.export.no_data")}
              errorMessage={t("session_log_view.export.failed")}
              generateCSV={(sortedData) =>
                generateSessionLogCSV(sortedData, courseTitle || "Course", hasVirtualSessions)
              }
            />
          )}
        </div>

        <SortableTable>
          <thead>
            <tr>
              <SortableHeader
                field="started_at"
                label={t("session_log_view.columns.started_at.label")}
                tooltip={t("session_log_view.columns.started_at.tooltip")}
              />
              <SortableHeader
                field="duration"
                label={t("session_log_view.columns.duration.label")}
                tooltip={t("session_log_view.columns.duration.tooltip")}
              />
              <SortableHeader
                field="class_type"
                label={t("session_log_view.columns.class_type.label")}
                tooltip={t("session_log_view.columns.class_type.tooltip")}
              />
              {canAuthorCourse && (
                <>
                  <SortableHeader
                    field="attendance"
                    label={t("session_log_view.columns.attendance.label")}
                    tooltip={t("session_log_view.columns.attendance.tooltip")}
                  />
                  {hasVirtualSessions && (
                    <>
                      <SortableHeader
                        field="student_talk_time"
                        label={t("session_log_view.columns.student_talk_time.label")}
                        tooltip={t("session_log_view.columns.student_talk_time.tooltip")}
                      />
                      <SortableHeader
                        field="instructor_talk_time"
                        label={t("session_log_view.columns.instructor_talk_time.label")}
                        tooltip={t("session_log_view.columns.instructor_talk_time.tooltip")}
                      />
                    </>
                  )}
                </>
              )}
              {canParticipateInCourse && (
                <>
                  <SortableHeader
                    field="your_attendance"
                    label={t("session_log_view.columns.your_attendance.label")}
                    tooltip={t("session_log_view.columns.your_attendance.tooltip")}
                  />
                  <SortableHeader
                    field="your_time_in_class"
                    label={t("session_log_view.columns.your_time_in_class.label")}
                    tooltip={t("session_log_view.columns.your_time_in_class.tooltip")}
                  />
                </>
              )}
              {hasVirtualSessions && (
                <SortableHeader
                  field="recording"
                  label={t("session_log_view.columns.recording.label")}
                  sortable={false}
                  tooltip={t("session_log_view.columns.recording.tooltip")}
                />
              )}
              {hasVirtualSessions && isMPAdmin && (
                <SortableHeader
                  field="recording_setup"
                  label={t("session_log_view.columns.recording_setup.label")}
                  sortable={false}
                  tooltip={t("session_log_view.columns.recording_setup.tooltip")}
                />
              )}
              {canAuthorCourse && <th aria-label="Options" className="w-4" />}
            </tr>
          </thead>

          <SortableBody<ClassSessionSummaryType>
            emptyColSpan={nColumns}
            emptyMessage={t("session_log_view.no_data")}
          >
            {(sortedData) =>
              sortedData.map((classSession) => (
                <ClassSessionRow
                  classSession={classSession}
                  hasVirtualSessions={hasVirtualSessions}
                  key={classSession.class_session_id}
                />
              ))
            }
          </SortableBody>
        </SortableTable>
      </SortableTableWrapper>
    </div>
  );
};
